import { Box, Button, Group, Skeleton, Stack, Text, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconPlayerPause, IconPlayerPlay, IconRepeat } from '@tabler/icons-react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useAuth } from '~/Auth'
import { useAlbum, useAlbumTracks, useSimilarAlbums, useUser } from '~/api/queries'
import { AlbumGrid } from '~/components/blocks/AlbumGrid/AlbumGrid'
import { CoverArt } from '~/components/blocks/CoverArt/CoverArt'
import { DownloadButton } from '~/components/blocks/DownloadButton/DownloadButton'
import { EnqueueButton } from '~/components/blocks/EnqueueButton'
import { FavoriteButton } from '~/components/blocks/FavoriteButton/FavoriteButton'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import { trackToQueueItem } from '~/components/player/utils'
import { usePlayer } from '~/hooks/use-player'
import { useShowDownloads } from '~/hooks/use-show-downloads'
import { formatDurationLong } from '~/lib/format'
import { trackToDownloadMeta } from '~/lib/offline/downloads'

export const Route = createFileRoute('/_app/albums/$albumId')({
  component: AlbumDetailPage,
})

function AlbumDetailPage() {
  const { albumId: albumIdParam } = Route.useParams()
  const albumId = Number(albumIdParam)
  const { session } = useAuth()
  const currentUserId = (session.data?.user as { id?: number } | undefined)?.id ?? 0
  const { data: currentUser } = useUser(currentUserId)
  const showNonLibrary = currentUser?.config.ui.showNonLibraryResults ?? false

  const { data: album, isLoading } = useAlbum(albumId)
  const { data: tracksData } = useAlbumTracks(albumId, { limit: 500 })
  const { data: similarAlbumsData } = useSimilarAlbums(albumId, showNonLibrary)
  const player = usePlayer()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const showDownloads = useShowDownloads()

  if (isLoading) return <Skeleton h={400} />
  if (!album) return <Text>Album not found</Text>

  const tracks = tracksData?.data ?? []
  const currentTrack = player.state.queue[player.state.currentIndex]
  const isThisAlbumContext = !player.state.radioMode && currentTrack?.albumId === albumId
  const isPlayingThisAlbum = isThisAlbumContext && player.state.isPlaying
  const isAtEnd =
    isThisAlbumContext &&
    player.state.currentIndex === player.state.queue.length - 1 &&
    !player.state.isPlaying

  let playButtonIcon = <IconPlayerPlay size={16} />
  let playButtonLabel = 'Play All'
  if (isPlayingThisAlbum) {
    playButtonIcon = <IconPlayerPause size={16} />
    playButtonLabel = 'Pause'
  } else if (isAtEnd) {
    playButtonIcon = <IconRepeat size={16} />
    playButtonLabel = 'Replay All'
  }

  const handlePlayAll = () => {
    if (isPlayingThisAlbum) {
      player.pause()
    } else if (isAtEnd) {
      player.play(tracks.map(trackToQueueItem))
    } else if (isThisAlbumContext) {
      player.resume()
    } else if (tracks.length > 0) {
      player.play(tracks.map(trackToQueueItem))
    }
  }

  return (
    <Stack gap="lg">
      <Group align="flex-start" gap="xl" wrap={isMobile ? 'wrap' : 'nowrap'}>
        <Box
          style={{
            width: isMobile ? '100%' : 250,
            flexShrink: 0,
          }}
        >
          <CoverArt
            type="albums"
            id={album.id}
            alt={album.name}
            size={500}
            w={isMobile ? '100%' : 250}
            h={isMobile ? undefined : 250}
            aspectRatio={isMobile ? 1 : undefined}
          />
        </Box>
        <Stack gap="xs" style={{ flex: 1, width: '100%' }}>
          <Title order={1}>{album.name}</Title>
          <Text
            renderRoot={(props) => (
              <Link
                {...props}
                to="/artists/$artistId"
                params={{ artistId: String(album.artistId) }}
              />
            )}
            size="lg"
            c="dimmed"
            td="none"
          >
            {album.artist}
          </Text>
          <Group gap="xs">
            {album.year && <Text c="dimmed">{album.year}</Text>}
            <Text c="dimmed">&middot;</Text>
            <Text c="dimmed">{album.trackCount} tracks</Text>
            <Text c="dimmed">&middot;</Text>
            <Text c="dimmed">{formatDurationLong(album.duration)}</Text>
          </Group>
          <Group mt="sm">
            <Button leftSection={playButtonIcon} onClick={handlePlayAll}>
              {playButtonLabel}
            </Button>
            {tracks.length > 0 && <EnqueueButton tracks={tracks.map(trackToQueueItem)} />}
            {showDownloads && tracks.length > 0 && (
              <DownloadButton variant="button" tracks={tracks.map(trackToDownloadMeta)} />
            )}
            <FavoriteButton type="album" id={album.id} favorited={album.favorited} />
          </Group>
        </Stack>
      </Group>

      {tracks.length > 0 && <TrackTable tracks={tracks} showAlbum={false} showNumber />}

      {similarAlbumsData?.data && similarAlbumsData.data.length > 0 && (
        <section>
          <Title order={3} mb="md">
            Similar Albums
          </Title>
          <AlbumGrid albums={similarAlbumsData.data} />
        </section>
      )}
    </Stack>
  )
}
